/**
 * Utility functions for managing meta tags dynamically
 */

/**
 * Sets or updates a meta tag in the document head
 */
function setMetaTag(attribute: string, value: string, content: string): void {
  let metaTag = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${value}"]`
  );
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    if (attribute === 'name') {
      metaTag.name = value;
    } else if (attribute === 'property') {
      metaTag.setAttribute('property', value);
    }
    document.head.appendChild(metaTag);
  }
  
  metaTag.content = content;
}

/**
 * Removes a meta tag from the document head
 * Reserved for future use
 */
// @ts-expect-error - Reserved for future use
function removeMetaTag(_attribute: string, _value: string): void {
  // Reserved for future implementation
}

/**
 * Sets the page title
 */
export function setPageTitle(title: string): void {
  document.title = title;
}

/**
 * Sets the meta description
 */
export function setMetaDescription(description: string): void {
  setMetaTag('name', 'description', description);
}

/**
 * Sets OpenGraph meta tags
 */
export function setOpenGraphTags(config: {
  title: string;
  description: string;
  type?: string;
  url?: string;
  image?: string;
}): void {
  setMetaTag('property', 'og:title', config.title);
  setMetaTag('property', 'og:description', config.description);
  setMetaTag('property', 'og:type', config.type || 'website');
  
  if (config.url) {
    setMetaTag('property', 'og:url', config.url);
  }
  
  if (config.image) {
    setMetaTag('property', 'og:image', config.image);
  }
}

/**
 * Sets Twitter Card meta tags
 */
export function setTwitterCardTags(config: {
  card?: string;
  title: string;
  description: string;
  image?: string;
}): void {
  setMetaTag('name', 'twitter:card', config.card || 'summary_large_image');
  setMetaTag('name', 'twitter:title', config.title);
  setMetaTag('name', 'twitter:description', config.description);
  
  if (config.image) {
    setMetaTag('name', 'twitter:image', config.image);
  }
}

/**
 * Sets all meta tags according to VerifyPH specifications
 */
export function setVerifyPHMetaTags(config?: {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
}): void {
  // Default values from METATAGS.md
  const defaultTitle = 'VerifyPH — Transparency by the People, for the People';
  const defaultDescription = 'VerifyPH is a citizen-powered transparency platform for documenting and verifying government-funded projects and public services through evidence-based reporting.';
  const defaultOGTitle = 'VerifyPH — Citizen-Powered Transparency';
  const defaultOGDescription = 'A public platform where citizens document and verify government-funded projects and public services using evidence, location data, and transparent moderation.';
  const defaultTwitterTitle = 'VerifyPH — Transparency by the People';
  const defaultTwitterDescription = 'Evidence-based citizen reporting for public service and government project transparency.';
  
  // Get base URL
  const baseUrl = config?.url || window.location.origin;
  const ogImage = config?.image || `${baseUrl}/og-image.png`;
  
  // Set title
  setPageTitle(config?.title || defaultTitle);
  
  // Set meta description
  setMetaDescription(config?.description || defaultDescription);
  
  // Set OpenGraph tags
  setOpenGraphTags({
    title: defaultOGTitle,
    description: defaultOGDescription,
    type: 'website',
    url: baseUrl,
    image: ogImage,
  });
  
  // Set Twitter Card tags
  setTwitterCardTags({
    card: 'summary_large_image',
    title: defaultTwitterTitle,
    description: defaultTwitterDescription,
    image: ogImage,
  });
}
